import React, { FC, useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Logo from "shared/Logo/Logo";
import MenuBar from "shared/MenuBar/MenuBar";
import AvatarDropdown from "./AvatarDropdown";
import LoginDropdown from "./LoginDropdown";
import Navigation from "shared/Navigation/Navigation";
import {
  getList as onGetList,
  updateData as onUpdateData,
  addNewData as onAddNewData,
  getData as onGetData,
  clearData,
  logoutCustomer
} from "store/AppMaster/actions";
import api from "constants/api";
import { RootState } from 'store/reducers';
import { useCart } from "context/CartContext"; // Import the useCart hook
import AddCart from "../../../src/images/addToCart_red.png"

export interface MainNav2LoggedProps { }

const MainNav2Logged: FC<MainNav2LoggedProps> = () => {
  const { cartCount, setCart } = useCart();

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchParam = new URLSearchParams(location.search).get("search");

  const [authCustomerDetails, setCustomerDetails] = useState<any>("");
  const [updateListData, setUpdateListData] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchForm, setShowSearchForm] = useState(false);

  const categoryData = localStorage.getItem("categoryData");
  const { data } = useSelector((state: RootState) => state.AppMaster);

  const inputRef = useRef<HTMLInputElement>(null);

  // ✅ Fetch category data
  useEffect(() => {
    if (!categoryData) {
      getListData();
    }

    // ✅ Load logged-in customer details if any
    const storedDetails = localStorage.getItem("AuthCustomerDetails");
    if (storedDetails) {
      setCustomerDetails(JSON.parse(storedDetails));
    }

    // ✅ Set search query if present in URL
    if (searchParam) {
      setSearchQuery(searchParam);
    }
  }, [categoryData, searchParam]);

  // ✅ Handle category list fetch
  const getListData = () => {
    setUpdateListData(true);
    dispatch(clearData());
    dispatch(onGetList({}, "", api.frontCategory));
  };

  useEffect(() => {
    if (data && updateListData && data.category && data.category.length > 0) {
      setUpdateListData(false);
      localStorage.setItem("categoryData", JSON.stringify(data.category));
    }
  }, [data, updateListData]);

  // ✅ Optimized: Load cached cart instantly, then refresh via API
  useEffect(() => {
    const storedDetails = localStorage.getItem("AuthCustomerDetails");
    if (storedDetails) {
      setCustomerDetails(JSON.parse(storedDetails));
    } else {
      setCustomerDetails("");
    }

    // 1️⃣ Load from localStorage instantly
    const storedCartCount = parseInt(localStorage.getItem("cartCount") || "0");
    const storedCartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    setCart(storedCartItems, storedCartCount);

    // 2️⃣ Fetch latest data in background
    getCartData();
  }, [location.pathname]);

  // ✅ Fetch latest cart data (optimized)
  const getCartData = async () => {
    const storedDetails = localStorage.getItem("AuthCustomerDetails");
    const storedToken = localStorage.getItem("AuthCustomer");

    const email = storedDetails ? JSON.parse(storedDetails).email : null;
    const token = storedToken ? JSON.parse(storedToken) : null;

    if (!email || !token) {
      setCart([], 0);
      localStorage.setItem("cartCount", "0");
      localStorage.setItem("cartItems", "[]");
      return;
    }

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + api.frontEndCartData,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      if (response.status === 401) {
        const result = await response.json(); // 🔹 integrated logic

        console.error("Unauthorized: Invalid or missing token");
        dispatch(logoutCustomer(navigate, "/login"));

        // Only redirect to login if not on a public page
        const publicPaths = [
          "/about",
          "/contact",
          "/category",
          "/terms-and-conditions",
          "/faqs",
          "/carriers",
          "/cancellation-and-refund-policy",
          "/privacy-policy",
          "/shipping-and-delivery-policy",
          "/mould-accessories",
          "/press-tool-accessories",
          "/die-polishing-accessories"
        ];

        if (!publicPaths.includes(location.pathname) && location.pathname !== "/login") {
          navigate("/login", { state: { sessionMessage: result.message } }); // 🔹 integrated logic
        }

        setCart([], 0);
        localStorage.setItem("cartCount", "0");
        localStorage.setItem("cartItems", "[]");
        return;
      }

      const result = await response.json();
      if (result.statusCode === 200 && result.data?.cart?.length > 0) {
        const cartItems = result.data.cart[0].detailed_codes || [];
        const count = result.data.cart[0].count || 0;

        // ✅ Optimistically update cartCount if localStorage has a higher value
        const storedCount = parseInt(localStorage.getItem("cartCount") || "0");
        const finalCount = storedCount > count ? storedCount : count;

        setCart(cartItems, finalCount);
        localStorage.setItem("cartCount", finalCount.toString());
        localStorage.setItem("cartItems", JSON.stringify(cartItems));
      } else {
        setCart([], 0);
        localStorage.setItem("cartCount", "0");
        localStorage.setItem("cartItems", "[]");
      }
    } catch (error) {
      console.error("Failed to fetch cart data:", error);
      setCart([], 0);
      localStorage.setItem("cartCount", "0");
      localStorage.setItem("cartItems", "[]");
    }
  };

  // ✅ Navigate to cart page
  const handleCartClick = () => {
    navigate("/cart");
  };

  // ✅ Search bar functionality
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const queryString = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : "";
    navigate("/products" + queryString);
  };

  const handleClose = () => {
    setShowSearchForm(false);
    setSearchQuery("");
    navigate("/category");
  };

  const renderMagnifyingGlassIcon = () => (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 22L20 20"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const renderSearchForm = () => (
    <form
      onSubmit={handleFormSubmit}
      className="flex-1 py-2 text-slate-900 dark:text-slate-100"
    >
      <div className="bg-slate-50 dark:bg-slate-800 flex items-center space-x-1.5 px-5 h-full rounded">
        {renderMagnifyingGlassIcon()}
        <input
          ref={inputRef}
          type="text"
          placeholder="Enter product code or product name"
          className="border-none bg-transparent focus:outline-none focus:ring-0 w-full text-base"
          autoFocus
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button type="button" onClick={handleClose}>
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <input type="submit" hidden value="" />
    </form>
  );

  const renderContent = () => (
    <div className="h-20 flex justify-between">
      {/* Left: Mobile Menu */}
      <div className="flex items-center xl:hidden xl:flex-1">
        <MenuBar />
      </div>

        <div className="lg:flex-1 flex items-center">
          <Logo className="flex-shrink-0" />
        </div>

      {/* Center: Navigation or Search */}
      <div className="flex-[2] hidden lg:flex justify-center mx-4">
        {showSearchForm ? renderSearchForm() : <Navigation />}
      </div>

      {/* Right: Search, Profile, Cart */}
      <div className="flex-2 flex items-center justify-end text-slate-700 dark:text-slate-100">
        {!showSearchForm && (
          <button
            className="hidden lg:flex w-10 h-10 sm:w-12 sm:h-12 rounded-full text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none items-center justify-center"
            onClick={() => setShowSearchForm(!showSearchForm)}
          >
            {renderMagnifyingGlassIcon()}
          </button>
        )}

        {/* Avatar or Login */}
        {authCustomerDetails ? <AvatarDropdown /> : <LoginDropdown />}

        {/* Cart Icon */}
        <button className="relative ml-3" onClick={handleCartClick}>
          {cartCount > 0 && (
            <div className="w-5 h-5 flex items-center justify-center bg-primary-500 absolute -top-[4px] -right-[1px] rounded-full text-[10px] leading-none text-white font-medium">
              <span className="mt-[1px]">{cartCount}</span>
            </div>
          )}
          {/* <svg
            className="w-6 h-6"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 2H3.74C4.82 2 5.67 2.93 5.58 4L4.75 13.96C4.61 15.59 5.9 16.99 7.54 16.99H18.19C19.63 16.99 20.89 15.81 21 14.38L21.54 6.88C21.66 5.22 20.4 3.87 18.73 3.87H5.82"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.25 22C16.94 22 17.5 21.44 17.5 20.75C17.5 20.06 16.94 19.5 16.25 19.5C15.56 19.5 15 20.06 15 20.75C15 21.44 15.56 22 16.25 22Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.25 22C8.94 22 9.5 21.44 9.5 20.75C9.5 20.06 8.94 19.5 8.25 19.5C7.56 19.5 7 20.06 7 20.75C7 21.44 7.56 22 8.25 22Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 8H21"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg> */}
          <img src={AddCart} alt="add cart" className="w-[25px]" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="nc-MainNav2Logged relative z-10 bg-white dark:bg-neutral-900 border-b border-slate-100 dark:border-slate-700">
      <div className="container">{renderContent()}</div>
    </div>
  );
};

export default MainNav2Logged;